import { FC, useEffect, useRef } from 'react';
import { Button } from '../../../../shared/Button';
import { Texts } from '../../../../shared/Texts';
import styles from './SendEmail.module.scss';
import { ValidationTypes } from './types';
import { useAppDispatch, useAppSelector } from 'redux/store';
import {
  selectExaminationID,
  selectReportRemainingResends,
  selectReportSendingisFinished,
  selectReportSendingisLoading,
  selectReportSendingisSucceeded,
} from 'redux/examination/selectors';
import { postEmailSending } from 'redux/examination/thunks';
import { validateEmail } from './emailValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { emailSchema } from './schema';
import { Email } from 'api/maintestApi';
import { TextField } from '@mui/material';

interface SendEmailProps {
  className?: string;
}

export const SendEmail: FC<SendEmailProps> = ({ className }) => {
  const dispatch = useAppDispatch();
  const isFinished = useAppSelector(selectReportSendingisFinished);
  const isSucceeded = useAppSelector(selectReportSendingisSucceeded);
  const isLoading = useAppSelector(selectReportSendingisLoading);
  const examinationId = useAppSelector(selectExaminationID);
  const remainingResends = useAppSelector(selectReportRemainingResends);
  const emailValue = useRef('');

  useEffect(() => {
    if (isFinished) {
      const isEmailValidFinal = validateEmail(
        emailValue.current,
        remainingResends,
        isSucceeded
      );
      if (!isEmailValidFinal.errorStrng.message) return;
      setError('email', {
        message: isEmailValidFinal.errorStrng.message ?? '',
        type: isSucceeded ? ValidationTypes.success : ValidationTypes.error,
      });
    }
  }, [isFinished, remainingResends]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<Email>({
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
    delayError: 3000,
    resolver: remainingResends > 0 ? yupResolver(emailSchema) : undefined,
  });

  const onSubmit = (val: Email) => {
    const data = {
      id: examinationId,
      email: val.email,
    };

    void dispatch(postEmailSending(data));
  };

  return (
    <div className={`${styles.send_email} ${className ?? ''}`}>
      <Texts className={styles.email_desc}>
        {'Nie martw się, zachowamy Twoją anonimowowść!'}
      </Texts>
      <label htmlFor={'email_Textinput'}>
        <form
          className="form"
          onSubmit={(event) => void handleSubmit(onSubmit)(event)}
        >
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                id={'email_Textinput'}
                className={`${styles.email_input} ${
                  styles[
                    errors.email?.type === 'matches'
                      ? 'error'
                      : (errors.email?.type as ValidationTypes)
                  ]
                }`}
                error={
                  errors.email?.type === 'success'
                    ? !errors.email
                    : !!errors.email
                }
                disabled={remainingResends === 0}
                placeholder={'Wpisz swój adres e-mail...'}
                aria-label="Wpisz swój adres e-mail na który przesłany zostanie wynik testu"
                {...field}
              />
            )}
          />
          {errors.email?.message && (
            <div
              className={`${styles.email_error} ${
                styles[
                  errors.email.type === 'matches'
                    ? 'error'
                    : (errors.email?.type as ValidationTypes)
                ]
              }`}
              role="alert"
            >
              <Texts className={styles.error_text}>
                <span className={styles.text_icon}></span>
                {errors.email?.message}
              </Texts>
            </div>
          )}
          <Button
            className={styles.send_button}
            disabled={
              isLoading ||
              errors.email?.type === 'matches' ||
              (remainingResends === 0 && errors.email?.type === 'error')
            }
            buttonType="submit"
            ariaLabel="Prześlij wynik testu na adres e-mail"
          >
            {'Prześlij'}
          </Button>
        </form>
      </label>
    </div>
  );
};
